<template>
  <div class="choose-other-plan-section">
    <template v-if="!futurePlan">
      <h4 class="choose-other-plan-section__title">
        {{ $tc('PaidPlanChange.EditPage.selectPaidPlan') }}
      </h4>
      <toggle-radio-card-list-section>
        <toggle-radio-card-parts
          v-for="(plan, index) in otherPlanList"
          :key="`planToggleCard${index}`"
          :radioValue="plan.planGroupId"
          :currentValue="selectedPlanGroupId"
          @handlerOnClick="handlerPlanSelect"
        >
          <plan-card-parts
            :plan="plan"
            :bargainPriced="bargainPriced(plan)"
            :procedureScreen="procedureScreen"
          />
        </toggle-radio-card-parts>
      </toggle-radio-card-list-section>

      <ul class="choose-other-plan-section__attention" v-html="attention" />
    </template>
    <!-- 将来のプランが存在する場合 -->
    <template v-else>
      <current-plan-card-section
        :sectionTitle="futurePlanTitle"
        :plan="futurePlan"
        :contractTerm="scheduledContractTerm"
        :isConfirmed="true"
      />
      <ul
        class="choose-other-plan-section__attention choose-other-plan-section__future"
        v-html="attention"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import ToggleRadioCardListSection from '@/components/common/ToggleRadioCard/ToggleRadioCardListSection.vue'
import ToggleRadioCardParts from '@/components/common/ToggleRadioCard/ToggleRadioCardParts.vue'
import PlanCardParts from '@/components/SignupPage/common/PlanCardParts.vue'
import CurrentPlanCardSection from '@/components/SignupPage/common/CurrentPlanCardSection.vue'
import ContractPlanDocument, {
  PaidPlanGroupType,
} from '@/store/stores/collectionModule/documents/plan/ContractPlanDocument'
import StoreUtil from '@/store/StoreUtil'
import i18n from '@/locales/I18n'
import {
  ContractTermType,
  ProcedureScreenType,
} from '@/store/stores/collectionModule/documents/GeneralTypes'
import useDisplayDependingOnLang from '@/components/hook/useDisplayDependingOnLang'

/**
 * プラン変更(有料=>有料) プラン選択
 */
export default defineComponent({
  name: 'ChooseOtherPlanSection',
  components: {
    ToggleRadioCardListSection,
    ToggleRadioCardParts,
    PlanCardParts,
    CurrentPlanCardSection,
  },
  props: {
    /**
     * 契約中以外のプラン一覧
     */
    otherPlanList: {
      type: Array as () => Array<ContractPlanDocument>,
      default: () => [],
    },
    /**
     * 将来のプラン
     */
    futurePlan: {
      type: Object as () => ContractPlanDocument,
      default: () => ({}),
    },
    /**
     * 選択中のプラングループID（プラン）
     */
    selectedPlanGroupId: {
      type: String as PropType<PaidPlanGroupType>,
      required: true,
    },
    /**
     * 契約中のプランの契約期間
     */
    contractTerm: {
      type: Object as PropType<ContractTermType>,
      default: () => ({}),
    },
    /**
     * 将来のプランの契約期間
     */
    scheduledContractTerm: {
      type: Object as PropType<ContractTermType>,
      default: () => ({}),
    },
    /**
     * どの手続き画面か
     */
    procedureScreen: {
      type: String as PropType<ProcedureScreenType>,
      default: null,
    },
  },
  setup(props, context) {
    const { getDisplayDate } = useDisplayDependingOnLang()

    const mypagePageStore = StoreUtil.useStore('MypagePageStore')
    const { monthlyPlanPriceJpy, annualPlanPriceJpy } = mypagePageStore

    /** プランが切り替わる日付 */
    const endDate = props.contractTerm.contractEndDate as number
    const futurePlanStartDate = getDisplayDate(endDate + 1)
    /** 注記 */
    const attention = computed(() =>
      !props.futurePlan
        ? i18n
            .t('PaidPlanChange.EditPage.changePlanAttention', {
              date: futurePlanStartDate,
            })
            .toString()
        : i18n
            .t('PaidPlanChange.EditPage.hasFuturePlanAttention', {
              date: futurePlanStartDate,
            })
            .toString(),
    )

    /** 将来のプランがある場合の見出し */
    const futurePlanTitle = i18n
      .t('MypagePage.plan.futurePlan', {
        date: futurePlanStartDate,
      })
      .toString()

    /** 月払いプランと比べて、年払いプランを契約した場合のお得額を計算 */
    const bargainPriced = (plan: ContractPlanDocument) => {
      if (plan.planGroupId === 'monthlyPlan') {
        /** 月払いはお得にならない */
        return ''
      }

      // お得額
      return `${(
        100 -
        (annualPlanPriceJpy.value / (monthlyPlanPriceJpy.value * 12)) * 100
      ).toFixed()}%`
    }

    /**
     * プラン選択通知
     * @param val
     */
    const handlerPlanSelect = (val: PaidPlanGroupType) => context.emit('handlerPlanSelect', val)

    return {
      attention,
      futurePlanTitle,
      bargainPriced,
      handlerPlanSelect,
    }
  },
})
</script>

<style lang="scss" scoped>
.choose-other-plan-section {
  padding-bottom: 24px;

  &__title {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 72px;
    padding: 0 0.5em;
    @include fontSetTextMedium(17);

    text-align: center;
    letter-spacing: 0.04em;
    font-feature-settings: 'palt' on;
  }

  &__attention {
    display: flex;
    flex-flow: column nowrap;
    align-items: flex-start;
    align-content: flex-start;
    justify-items: flex-start;
    justify-content: flex-start;
    width: 100%;
    max-width: 800px;
    margin: 24px auto 0;
    padding: 0 16px;
    text-align: left;
    @include fontSetTextMedium(12);

    color: $black50;
  }

  &__future {
    margin: 0 auto;
  }
}
</style>

<style lang="scss">
.choose-other-plan-section {
  &__attention {
    li::before {
      display: inline-block;
      content: '*';
    }
  }
}
</style>
